
























































import Vue, { ComputedOptions } from 'vue'
import Chat from 'twilio-chat'
import { get, dispatch } from 'vuex-pathify'

import { ChatMessage, Booking } from '@/models'
import { Channel } from 'twilio-chat/lib/channel'
import { Member, MemberDescriptor } from 'twilio-chat/lib/member'
import { User } from 'twilio-chat/lib/user'
import { Paginator } from 'twilio-chat/lib/interfaces/paginator'
import { Message } from 'twilio-chat/lib/message'
import { concat, once } from 'lodash'

interface options extends Vue {
  $refs: {
    fileUploader: Vue | any,
    messages: Vue | any
  },
  channel: null | Channel,
  booking: Booking,
  setChannel: Function,
  openChat: Function,
  attachEventListeners: Function
}

export default Vue.extend<options>().extend({
  props: {
    otherUser: {
      type: Object,
      required: true
    },
    booking: {
      type: Object,
      required: true,
    },
    textClass: {
      type: String,
      default: 'paragraph--small'
    },
    hideButton: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      channel: null as null | Channel,
      message: {
        text: '',
        file: null as null | FormData,
        imageData: null as null | Object
      },
      isConnected: false,
      isSendingMedia: false,
      showChat: false,
      messages: [] as ChatMessage[],
      page: null as null | Paginator<Message>,
      messagesCount: 0,
      lastConsumedMessageCount: 0,
      otherPersonOnline: false,
      isTyping: false,
      attachEventListenersOnce: once(() => {
        this.attachEventListeners()
      }) as Function
    }
  },
  computed: {
    authenticatedUser: get('auth/user') as ComputedOptions<any>,
    // for some reason watching both messages count and lastconsumedmessagecount
    // doesn't work so I did this to emit the value
    notificationCount () {
      this.$emit('notification', Math.max(0, this.messagesCount - this.lastConsumedMessageCount))
      return this.messagesCount - this.lastConsumedMessageCount
    }
  },
  // watchers are buggy
  /* tslint:disable */
  watch: {
    message: {
      deep: true,
      handler () {
        // typing indicator
        if (this.channel) {
          this.channel.typing()
        }
        this.scrollToEnd(true)
      }
    },
    async channel () {
      if (!this.channel) {
        return
      }

      this.messagesCount = await this.channel!.getMessagesCount()

      this.attachEventListenersOnce()

      // if the chat is open, get the messages
      if (this.showChat) {
        this.fetchRecentMessages()
      }
    }
  },
  /* tslint:enable */
  beforeDestroy () {
    if (this.channel) {
      this.channel.removeAllListeners()
    }
    this.channel = null
  },
  mounted () {
    // do not set the channel for new bookings, they need a couple of minutes to create the backend chat
    // if the user opens the chat we will give it a chance to do its thing
    if (!this.booking.isFreshBooking) {
      this.setChannel()
    }
  },
  methods: {
    async closeChat () {
      // update message count
      this.messagesCount = await this.channel!.getMessagesCount()
      this.showChat = false
    },
    async attachEventListeners () {
      // channel needs to exist
      if (!this.channel) {
        return
      }

      const members = await this.channel.getMembers()
      members.forEach(async (member: Member) => {
        // if there are messages and the member is me
        if (member.identity === this.authenticatedUser.id && this.messagesCount) {
          // update the last consumed message count
          this.lastConsumedMessageCount = member.lastConsumedMessageIndex + 1
          member.on('updated', (e : Member.UpdatedEventArgs) => {
            this.lastConsumedMessageCount = e.member.lastConsumedMessageIndex + 1
          })
        } else {
          const user = await member.getUser()
          // update the online status
          this.otherPersonOnline = user.online
          user.on('updated', (e : User.UpdatedEventArgs) => {
            this.otherPersonOnline = e.user.online
          })
        }
      })

      // listeners for new messages
      this.channel.on('messageAdded', (m : ChatMessage) => {
        if (!this.validMessage(m)) {
          return
        }
        this.messages.push(this.getMessageOptions(m))
        // only increment message count if not showing chat
        if (!this.showChat) {
          this.messagesCount += 1
        } else {
          // if there is a new message scroll to end
          this.scrollToEnd()
        }
      })

      this.channel.on('typingStarted', (m : MemberDescriptor) => {
        if (m.identity !== this.authenticatedUser.id) {
          this.isTyping = true
        }
      })

      this.channel.on('typingEnded', (m : MemberDescriptor) => {
        if (m.identity !== this.authenticatedUser.id) {
          this.isTyping = false
        }
      })
    },
    async setChannel () {
      const { token } = await dispatch('chat/getChatToken', this.booking)

      const client = await Chat.create(token, {
        logLevel: 'warn'
      })

      // if theres no channel and theres a client + token
      if (!this.channel && client && token) {
        // while the channel hasn't been created try and get the channel
        // this is a fix because sometimes it returns null (since the api is still creating it)
        setTimeout(async () => {
          const channelDescriptor = (await client.getUserChannelDescriptors()).items[0]
          this.channel = await channelDescriptor.getChannel()
        }, 1000)
      }
    },
    /**
     *
     * @param page
     * @returns [ChatMessage]
     */
    async processPage (page : Paginator<ChatMessage>) {
      const messages = page.items
      const chatMessages = []
      for (const message of messages) {
        if (this.validMessage(message)) {
          const messageOptions = await this.getMessageOptions(message)
          chatMessages.push(
            new ChatMessage(messageOptions)
          )
        } else {
          this.messagesCount -= 1
        }
      }
      return chatMessages
    },
    async prevPage () {
      if (!this.page || this.page!.hasPrevPage) {
        return
      }

      const page = (await this.page.prevPage()) as Paginator<ChatMessage>
      const past = await this.processPage(page)

      // get the current scroll height so that we can keep the messages scrolled to the same spot
      const el = this.$refs.messages.$refs.dialog
      const scrollTo = el.scrollHeight

      this.messages = concat(past, this.messages)
      this.page = page

      // scroll to the new height - old height
      if (scrollTo) {
        this.$nextTick(() => {
          el.scrollTop = el.scrollHeight - scrollTo
        })
      }
    },
    // for filtering out system messages
    validMessage (message : ChatMessage) {
      // TODO deprecate message.body = 'SYSTEM'
      // return true if the message isn't a userJoined message or has SYSTEM as the body
      // if user sends SYSTEM it won't be sent (not ideal)
      return !((message.attributes && message.attributes.userJoined) || message.body === 'SYSTEM')
    },
    // gets the last 20 messages
    async fetchRecentMessages () : Promise<void> {
      if (!this.channel) {
        return
      }

      if (!this.page) {
        // get the last 20 messages and process them
        this.page = await this.channel.getMessages(10)
      }

      // if no messages have been stored
      if (!this.messages.length) {
        this.messages = await this.processPage(this.page)

        await this.$nextTick(() => {
          this.isConnected = true
        })

        this.scrollToEnd(true) // force scroll to bottom when chat is opened
      }
    },
    async openChat () {
      this.showChat = true

      // update channel if there is none
      if (!this.channel) {
        await this.setChannel()
      } else {
        // if the channel has been connected, get the lastest messages
        this.fetchRecentMessages()
      }
    },
    /**
     * Mark all messages as read
     */
    async readAllMessages () {
      await this.channel!.setAllMessagesConsumed()
    },
    getMessageOptions (message: ChatMessage) {
      const author = message.author === this.authenticatedUser.id ? this.authenticatedUser : this.otherUser

      const messageOptions = {
        body: message.body,
        author,
        sent: author === this.authenticatedUser,
        id: message.sid,
        contentUrl: false,
        attributes: {},
        filename: null,
        isImage: false
      } as ChatMessage

      // if message is media type, get the media temp url
      if (message.type === 'media') {
        messageOptions.contentUrl = message.media.getContentTemporaryUrl() // the promise for getting the content URL from twilio
        // if there are any message attributes, set them
        if (message.attributes) {
          messageOptions.attributes = message.attributes
        }
        // get the filename and the type
        messageOptions.filename = message.media.filename
        messageOptions.isImage = message.media.contentType.includes('image')
      }
      return messageOptions
    },
    sendMedia () {
      this.isSendingMedia = true
      this.channel!.sendMessage(this.message.file!, {
        ...this.message.imageData // send the image data along with the file (if any)
      })
        .then(() => {
          this.message.file = null
          this.$refs.fileUploader.clear()
          this.isSendingMedia = false
        })
        .catch((err : any) => {
          this.$logger.error('Failed to send media message', err)
          this.$toast.error('Failed to send media message')
          this.isSendingMedia = false
        })
    },
    sendMessage () {
      const message = this.message.text.trim()

      // check if file exists before sending
      if (this.message.file) {
        this.sendMedia()
      }

      if (message && this.channel) {
        this.channel.sendMessage(message)
          .catch((err : any) => {
            this.$logger.error('Failed to send message', err)
            this.$toast.error('Failed to send message')
            if (!this.message.text.trim()) {
              this.message.text = message
            }
          })
        this.readAllMessages()
        this.message.text = ''
        this.$nextTick(() => {
          this.$refs.fileUploader.$refs.input.$refs.field.calculateInputHeight()
        })
      }
    },
    handleChatFocus () {
      // need a timeout otherwise it won't work
      setTimeout(() => {
        this.scrollToEnd(true)
      }, 100)
    },
    scrollToEnd (force = false) {
      const el = this.$refs.messages.$refs.dialog
      if (
        el && (
          force ||
          // We're scrolled within 450px of the bottom
          el.scrollTop >= el.scrollHeight - el.offsetHeight - 450
        )
      ) {
        this.$nextTick(() => {
          el.scrollTop = el.scrollHeight
          this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight
        })

        if (this.channel) {
          // mark all messages as read
          this.readAllMessages()
        }
      }
    },
    setMessage (e) {
      this.message = e
    }
  }
})
